import * as Yup from 'yup';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { ButtonActor, DigitalCodeInputActor, Text } from 'components';
import { createTransLinksComponents } from '../../utils/transLinksComponents';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { phoneMaskedSanitized } from 'utils/sanitizers';
import { SESSION_PHONE_UPDATED_KEY } from 'pages/PhoneVerificationChangePhone/PhoneVerificationChangePhone';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { usePhoneVerificationScreenLogic } from './usePhoneVerificationScreenLogic';
import classes from 'classnames';
import InfoIcon from 'assets/svg/info-green-v2.svg?react';
import layout from 'scss/layout.module.scss';
import OTPModal from 'components/OTPModal/OTPModal';
import Placeholder from 'components/Placeholder/Placeholder';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import RegistrationLoading from '../RegistrationLoading/RegistrationLoading';
import styles from './PhoneVerification.module.scss';
import useClient from '../../hooks/useClient';

export interface PhoneVerificationForm {
  verificationCode0?: string;
  verificationCode1?: string;
  verificationCode2?: string;
  verificationCode3?: string;
}

const phoneVerificationValidationSchema = Yup.object().shape({
  verificationCode0: Yup.string().required(),
  verificationCode1: Yup.string().required(),
  verificationCode2: Yup.string().required(),
  verificationCode3: Yup.string().required(),
});

const SECONDS_WAIT_RESEND_SMS = 60;

export default function PhoneVerification() {
  const { t } = useTranslation('phoneVerification');
  const [showError, setShowError] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [loadingVerification, setLoadingVerification] = useState(false);
  const [isPromisePending, setIsPromisePending] = useState(false);
  const {
    handleSubmit,
    loading,
    resendCode,
    navigateToChangePhone,
    initialSecondsLeftResend,
    initialSecondsLeftPhoneChange,
  } = usePhoneVerificationScreenLogic();
  const [isTimeoutDisplayed, setIsTimeoutDisplayed] = useState(
    initialSecondsLeftResend > 0,
  );
  const [timeLeft, setTimeLeft] = useState<number>(
    initialSecondsLeftResend > 0
      ? initialSecondsLeftResend
      : SECONDS_WAIT_RESEND_SMS,
  );
  const [isPhoneChangeTimeoutDisplayed, setIsPhoneChangeTimeoutDisplayed] =
    useState(initialSecondsLeftPhoneChange > 0);
  const [timeLeftPhoneChange, setTimeLeftPhoneChange] = useState<number>(
    initialSecondsLeftPhoneChange,
  );
  const { client, fetchClient } = useClient();

  const clientPhone =
    sessionStorage.getItem(SESSION_PHONE_UPDATED_KEY) ||
    client?.mobilePhone?.mobilePhone;

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const countdownPhoneChangeIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  const formik = useFormik<PhoneVerificationForm>({
    initialValues: {
      verificationCode0: undefined,
      verificationCode1: undefined,
      verificationCode2: undefined,
      verificationCode3: undefined,
    },
    validationSchema: phoneVerificationValidationSchema,
    onSubmit: async (data) => {
      setLoadingVerification(true);
      await handleSubmit(data);
      await fetchClient();
      setLoadingVerification(false);
    },
  });

  const continueBtnClick = async () => {
    const isFormValid = Object.keys(await formik.validateForm()).length === 0;
    if (!isFormValid) {
      setShowError(true);
    }
  };

  useEffect(() => {
    if (!isTimeoutDisplayed) return;

    countdownIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 1) return prev - 1;
        setIsTimeoutDisplayed(false);
        return prev;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [isTimeoutDisplayed]);

  useEffect(() => {
    if (!isPhoneChangeTimeoutDisplayed) return;

    countdownPhoneChangeIntervalRef.current = setInterval(() => {
      setTimeLeftPhoneChange((prev) => {
        if (prev > 1) return prev - 1;
        setIsPhoneChangeTimeoutDisplayed(false);
        return prev;
      });
    }, 1000);

    return () => {
      if (countdownPhoneChangeIntervalRef.current)
        clearInterval(countdownPhoneChangeIntervalRef.current);
    };
  }, [isPhoneChangeTimeoutDisplayed]);

  /* Logic needs to be implemented once we have some data from BE about validation.
  At this point you need to enter any 2+ digits to be allowed further */

  const formatTimeLeft = timeLeft > 9 ? timeLeft : '0' + timeLeft;
  const formatTimeLeftPhoneChange =
    timeLeftPhoneChange > 9 ? timeLeftPhoneChange : '0' + timeLeftPhoneChange;

  const handlePendingSms = useCallback(async () => {
    try {
      setIsPromisePending(true);
      // sms sent after change the phone number automatically
      const timeOutDisplayed = await resendCode();
      setTimeLeft(timeOutDisplayed || SECONDS_WAIT_RESEND_SMS);
      setIsTimeoutDisplayed(true);
    } finally {
      setIsPromisePending(false);
    }
  }, [resendCode]);

  // Initial call to send the sms code (resendCode handles cooldown internally)
  useEffect(() => {
    if (!initialSecondsLeftResend) {
      handlePendingSms();
    }
  }, []);

  const onChangeDigitalInput = () => {
    if (showError) {
      setShowError(false);
    }
  };

  const hanldeOTPModal = () => setShowOTPModal(!showOTPModal);

  if (loading) return <RegistrationLoading />;
  const classNameDigitalCode = showError ? styles['border-error'] : '';
  const borderRadius = showError ? '' : styles.border;

  const showTimer = isPromisePending || isTimeoutDisplayed;

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Confirmation}
      hideEditButton
    >
      <OTPModal isOpen={showOTPModal} onClose={hanldeOTPModal} />
      <form
        onSubmit={formik.handleSubmit}
        data-test="form"
        className={styles.form}
      >
        <div className={styles.title} data-test="css-Phone-Verification-Title">
          {t('heading')}
        </div>
        <div
          className={styles['subheading-wrapper']}
          data-test="css-Phone-Verification-Subtitle"
        >
          <span>{t('subheading')}</span>
          <Placeholder
            className={styles['subheading-mobile']}
            loading={!clientPhone}
            value={phoneMaskedSanitized(clientPhone)}
          />
        </div>
        <DigitalCodeInputActor
          classNameFieldGroup={classes(styles['field-group'], borderRadius)}
          className={classNameDigitalCode}
          formik={formik}
          onChange={onChangeDigitalInput}
          name="verificationCode"
          data-test="css-Phone-Verification-Code"
          analytics={{
            description: 'Verification code',
          }}
        />
        {showError && (
          <Text
            className={styles['error-text']}
            level={3}
            text={t('error_message')}
            dataTest="css-Phone-Verification-Error"
          />
        )}
        <div className={classes(styles['resend-code'])}>
          {showTimer ? (
            <div
              className={styles['phone-check']}
              data-test="css-Resend-Code-Time-1"
            >
              <Trans
                i18nKey="phoneVerification:time_remaining"
                components={createTransLinksComponents(
                  handlePendingSms,
                  'css-Resend-Code-Time-1-link',
                )}
                values={{
                  time: ` 00:${formatTimeLeft}`,
                }}
              />
            </div>
          ) : (
            <div
              className={styles['phone-check']}
              data-test="xpath-Resend-Code"
            >
              <Trans
                i18nKey="phoneVerification:resend_code"
                components={createTransLinksComponents(
                  handlePendingSms,
                  'css-Resend-Code-Time-2',
                  styles['link-here'],
                )}
                values={{
                  clientPhone: phoneMaskedSanitized(clientPhone),
                }}
              />
            </div>
          )}
        </div>
        <div className={styles['phone-check']} data-test="css-Info">
          {isPhoneChangeTimeoutDisplayed ? (
            <div data-testid="phoneVerification-phone-change-timer">
              {t('phone_change_time_remaining', {
                time: `00:${formatTimeLeftPhoneChange}`,
              })}
            </div>
          ) : (
            <Trans
              i18nKey="phoneVerification:change_phone_text_1"
              components={createTransLinksComponents(
                navigateToChangePhone,
                'phoneVerification-change-phone-text-1',
                styles['link-here'],
              )}
              values={{
                clientPhone: phoneMaskedSanitized(clientPhone),
              }}
            />
          )}
        </div>
        <div
          className={styles['phone-check-spam']}
          data-test="phoneVerification-spam-folder"
        >
          <InfoIcon />
          <div>
            <Trans
              i18nKey={'phoneVerification:spam_folder'}
              components={createTransLinksComponents(hanldeOTPModal)}
            />
          </div>
        </div>
        <ButtonActor
          buttonProps={{
            className: classes([styles.button, layout['width-sm']]),
            dataTest: 'css-Continue-Button',
            loading: loadingVerification,
            onClick: continueBtnClick,
            type: 'submit',
          }}
          formik={formik}
        >
          {t('submit_button')}
        </ButtonActor>
      </form>
    </Frame>
  );
}
